import { Typography } from "@material-ui/core";
import { useEffect, useMemo, useState } from "react";
import {
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanInput,
  DateInput,
  FormDataConsumer,
  FormTab,
  maxValue,
  minValue,
  NumberInput,
  RadioButtonGroupInput,
  ReferenceArrayInput,
  required,
  SaveButton,
  TabbedForm,
  TextInput,
  Toolbar,
  useGetList
} from "react-admin";
import {
  useQuizAssessmentAvailabilityDataProvider
} from "../../../providers/data-provider/quiz-assessment-availability.data.provider";
import { QuizAssessmentResultsDetailsPage } from "./quiz-assessment-results-details.component";

export function QuizAssessmentAvailabilityForm({
  disableFields,
  ...props
}) {
  const [isAssessmentStarted, setIsAssessmentStarted] = useState(false);
  const { listAvailabilityResultItem } = useQuizAssessmentAvailabilityDataProvider();

  useEffect(() => {
    listAvailabilityResultItem(props.record.id).then((result) => {
      for (const item of result.data) {
        if (item.startedAt) {
          setIsAssessmentStarted(true);
        }
      }
    })
  }, [listAvailabilityResultItem, props.record.id]);


  const parsedProps = useMemo(() => {
    return {
      ...props.record,
      question_bank: {
        ...props.record.question_bank,
        questions: null
      },
      classes: Array.isArray(props.record.classes) ? props.record.classes.map(w => w.id) : [],
      questions: Array.isArray(props.record.questions) ? props.record.questions.map(w => w.id) : [],
    }
  }, [props.record])

  const { data, ids } = useGetList(
    'quiz-assessment-question-bank', { page: 1, perPage: 99 }, { field: 'quiz-assessment-question-bank.id', order: 'ASC' }, {}
  );

  const parsed = ids.map((item) => data[item])

  const questionBanks = parsed;


  const CustomToolbar = (props) => (
    <Toolbar {...props}>
      <SaveButton />
    </Toolbar>
  );

  return (
    <TabbedForm
      {...props}
      record={parsedProps}
      toolbar={<CustomToolbar />}
      validate={(record) => {
        const startDate = new Date(record.start);
        const endDate = new Date(record.end);

        if (record.start && isNaN(startDate.getTime())) {
          return { start: 'Data de início inválida' };
        }
        if (record.end && isNaN(endDate.getTime())) {
          return { end: 'Data de fim inválida' };
        }
        if (record.start && record.end && startDate >= endDate) {
          return { start: 'A data inicial deve ser menor que a data final' };
        }
        return null;
      }}
    >
      <FormTab label={'Configurações da Avaliação'}>
        <RadioButtonGroupInput
          source={'assessmentType'}
          label={'Tipo de Avaliação'}
          choices={[
            { name: 'Online', id: 'online' },
            { name: 'Impressa', id: 'printed' }
          ]}
          validate={[required()]}
          disabled={isAssessmentStarted}
        />
        <AutocompleteInput
          label="Selecionar banco de questões"
          source="question_bank.id"
          choices={questionBanks}
          optionText={choice =>
            `${choice?.name} (${choice?.question_amount} questões)`
          }
          optionValue="id"
          fullWidth
          validate={[required()]}
          disabled={isAssessmentStarted}
        />
        <TextInput
          source={'name'}
          fullWidth
          label={'Nome da avaliação'}
          validate={required()}
        />
        <FormDataConsumer>
          {({ formData }) =>
            formData.assessmentType !== 'printed' && (
              <DateInput
                source={'start'}
                label={'Início da disponibilidade'}
                fullWidth
                disabled={isAssessmentStarted}
                validate={[required()]}
              />
            )
          }
        </FormDataConsumer>

        <FormDataConsumer>
          {({ formData }) => {
            const startDate = new Date(formData.start);
            if (formData.start && !isNaN(startDate.getTime()) && startDate > new Date()) {
              return (
                <Typography variant="body2" style={{ color: '#f57c00', marginTop: -8, marginBottom: 8 }}>
                  Atenção! Avaliação será programado para uma data futura!
                </Typography>
              );
            }
            return null;
          }}
        </FormDataConsumer>

        <FormDataConsumer>
          {({ formData }) =>
            formData.assessmentType !== 'printed' && (
              <DateInput
                source={'end'}
                label={'Fim da disponibilidade'}
                fullWidth
                disabled={isAssessmentStarted}
                required
              />
            )
          }
        </FormDataConsumer>

        <ReferenceArrayInput
          sort={{ field: "name", order: "ASC" }}
          perPage={10000}
          filter={{ academic_period: 'current', }}
          label="Turma"
          source="classes"
          multiple
          reference="classes"
          fullWidth
          validate={[required()]}
          disabled={isAssessmentStarted}
        >
          <AutocompleteArrayInput optionText={'name'} optionValue={'id'} multiple />
        </ReferenceArrayInput >


        <FormDataConsumer>
          {({ formData }) => {

            const selectedQuestionBank =
              questionBanks?.find(
                qb => qb.id === formData?.question_bank?.id
              );

            const maxQuestions =
              selectedQuestionBank?.question_amount || 1;

            return (
              <NumberInput
                source="questionAmount"
                label="Total de questões por avaliação"
                fullWidth
                min={1}
                max={maxQuestions}
                disabled={isAssessmentStarted}
                validate={[
                  required(),
                  minValue(1, "Mínimo permitido: 1"),
                  maxValue(
                    maxQuestions,
                    `Máximo permitido: ${maxQuestions}`
                  )
                ]}
              />
            );
          }}
        </FormDataConsumer>

        <FormDataConsumer>
          {({ formData }) =>
            formData.assessmentType !== 'printed' && (
              <NumberInput
                source="durationInMinutes"
                label="Duração da Avaliação (minutos)"
                disabled={isAssessmentStarted}
                fullWidth
                validate={[required()]}
              />
            )
          }
        </FormDataConsumer>

        <BooleanInput
          disabled={isAssessmentStarted || disableFields?.includes('randomizeQuestions')}
          defaultValue={false}
          source={'randomizeQuestions'}
          label={'Questões Aleatórias'}
          fullWidth
        />

        <FormDataConsumer>
          {({ formData }) =>
            !disableFields?.includes('disabled') && (
              <BooleanInput
                defaultValue={false}
                source={'disabled'}
                label={formData.disabled ? 'Reativar Avaliação' : 'Desativar Avaliação'}
                fullWidth
              />
            )}
        </FormDataConsumer>

      </FormTab>
      {/*{*/}
      {/*  props.record.id && (*/}
      {/*    <FormTab label={'Resultados'}>*/}
      {/*      <Results />*/}
      {/*    </FormTab>*/}
      {/*  )*/}
      {/*}*/}
    </TabbedForm>
  )
}

function Results(props) {
  if (!props.record.id) {
    return <Typography></Typography>;
  }
  return (
    <QuizAssessmentResultsDetailsPage availabilityId={props.record.id} assessmentName={props.record.name} assessmentType={props.record.assessmentType} />
  )
}
